import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import {
  supabase,
  readInitialUrlHash,
  markInitialGrantAccepted,
  initialGrantWasAccepted,
} from "@/integrations/supabase/client";
import { useAuthStore } from "@/hooks/useAuthStore";
import { SIGNED_OUT_MID_LOAD } from "@/stores/AuthStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { identifyUser } from "@/lib/posthog/client";
import { track } from "@/lib/posthog/events";
import { logger } from "@/utils/logger";
import {
  endOAuthRoundTrip,
  matchesOAuthRoundTrip,
  OAUTH_STATE_PARAM,
  safeNextPath,
} from "@/utils/authUtils";
import {
  AUTH_SECONDARY_ACTION_CLASSES,
  AuthPageHeader,
  AuthPageLayout,
} from "@/components/auth/AuthPageLayout";

type State = "verifying" | "failed";

/**
 * Budget for the whole callback: `getSession()` plus the store's profile / roles /
 * assignment fetches. Nothing here is guaranteed to settle — auth-js awaits its
 * `initializePromise` and then `_acquireLock(-1, …)`, an UNBOUNDED wait on
 * `navigator.locks`, so another tab hung mid-refresh while holding the auth lock
 * leaves the await neither resolving nor rejecting (loadSession takes the same lock).
 * Without a timer that is an eternal spinner with no way out. Longer than
 * ProtectedRoute's 10s for the same work because tripping this signs the user out.
 */
const CALLBACK_TIMEOUT_MS = 15000;

/**
 * /auth/callback — landing page for the OAuth redirect (Google).
 *
 * The Supabase client uses the supabase-js default implicit flow, so the session
 * arrives in the URL hash and `detectSessionInUrl` consumes it while the client
 * initialises. `getSession()` awaits that same initialisation, so a single call
 * here already sees the parsed session — no polling needed. What it does NOT see is
 * whether that session came from this round trip, hence the grant and state checks below.
 *
 * On failure Supabase still redirects here, with `error`, `error_code` and
 * `error_description` in the hash fragment (query string on some paths). Those
 * values are attacker-controllable, so they are logged, never rendered.
 */
/**
 * The fragment this page load arrived with, read from the snapshot the supabase client
 * module takes before it constructs the client — supabase-js clears the hash once it has
 * consumed the grant, so `window.location.hash` here is a race.
 */
function readCallbackHashParams() {
  // slice(1) rather than a /^#/ regex: Codacy's Lizard parser reads a regex literal as a
  // division operator and then attributes the rest of the file to this function.
  return new URLSearchParams(readInitialUrlHash().slice(1));
}

/**
 * Reads the failure Supabase reports on the redirect. It lands in the hash fragment,
 * and in the query string on some paths, so both are consulted.
 */
function readProviderError(searchParams: URLSearchParams) {
  const hashParams = readCallbackHashParams();
  const pick = (key: string) => hashParams.get(key) || searchParams.get(key);
  const error = pick("error");

  return error
    ? { error, errorCode: pick("error_code"), description: pick("error_description") }
    : null;
}

/**
 * Did THIS page load carry an implicit grant? The only thing here tied to the round trip.
 *
 * `getSession()` answers "is there a session", not "did this attempt create one": with no
 * callback params in the URL, `_initialize()` takes the storage-recovery path and hands
 * back whatever session storage already held. Accepting that navigates on — and logs a
 * successful sign-in — for an attempt that produced nothing, and on a shared browser drops
 * whoever clicked "Continue with Google" into the previous user's account.
 *
 * When the fragment IS present the session cannot be a stale one: that branch of
 * `_initialize()` either adopts the session the fragment carries or calls
 * `_removeSession()`, and never falls through to the stored session.
 *
 * What it does NOT establish is that the grant belongs to a sign-in anyone here started. The
 * implicit flow carries no `state` and no PKCE verifier, so an INVALID `#access_token=…` is
 * dropped by gotrue, but a valid one minted on an attacker's own account is adopted just as
 * readily as ours — hence the round-trip state, checked next.
 *
 * The query is read as well as the fragment because gotrue reads both: `parseParametersFromURL`
 * merges the hash params and then `url.searchParams` over them, and `_isImplicitGrantCallback`
 * matches on `params.access_token` wherever it came from (auth-js 2.70). So
 * `/auth/callback?access_token=…&refresh_token=…&expires_in=…&token_type=bearer` is adopted and
 * persisted while the client initialises, exactly like the fragment form — and nothing clears it
 * afterwards, since `_getSessionFromURL` only assigns `window.location.hash = ''`. Reading the
 * fragment alone would call that "no grant", take the non-destructive path below, and leave the
 * attacker's session in localStorage for every later page load to hydrate.
 */
function hasImplicitGrant(searchParams: URLSearchParams) {
  return readCallbackHashParams().has("access_token") || searchParams.has("access_token");
}

type CallbackRejection = {
  code: string;
  message: string;
  data?: Record<string, string | null>;
  /** Whether this URL already minted a session, which then has to be dropped. */
  forgedGrant?: boolean;
};

/**
 * Why this redirect cannot have signed anyone in, or null if it still might have.
 *
 * Every answer is settled by the URL alone, so they are read before any session is —
 * see hasImplicitGrant for why looking at the session first is not enough.
 */
function readCallbackRejection(searchParams: URLSearchParams): CallbackRejection | null {
  const providerError = readProviderError(searchParams);
  if (providerError) {
    return {
      code: "oauth-callback-error",
      message: "OAuth provider returned an error",
      data: providerError,
    };
  }

  if (!hasImplicitGrant(searchParams)) {
    return { code: "oauth-callback-no-grant", message: "OAuth redirect carried no session" };
  }

  // A grant nobody in this browser asked for — a link someone else wrote. See
  // beginOAuthRoundTrip: the grant may be perfectly valid and still be the attacker's.
  if (!matchesOAuthRoundTrip(searchParams.get(OAUTH_STATE_PARAM))) {
    return {
      code: "oauth-callback-state-mismatch",
      message: "OAuth redirect did not carry this browser's sign-in state",
      forgedGrant: true,
    };
  }

  return null;
}

export default function AuthCallback() {
  const { t } = useLanguage();
  const [state, setState] = useState<State>("verifying");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const authStore = useAuthStore();

  // `next` arrives from the link, so it is untrusted — see safeNextPath.
  const next = safeNextPath(searchParams.get("next"), "/buildings");

  useEffect(() => {
    let cancelled = false;
    // Set once this page starts hydrating the store, which is the only point past
    // which a failure leaves state that has to be dropped — see failAndSignOut.
    let hydrating = false;
    // Set once the outcome is on screen (or a navigation is issued), which is what
    // makes the timeout below moot — NOT when `run()` settles: the catch path awaits
    // logout() before showing anything, and that sign-out takes the very lock that
    // hangs, so a timer cancelled on settle would leave that path spinning.
    let settled = false;

    const showFailure = () => {
      settled = true;
      if (!cancelled) setState("failed");
    };

    // Only for a failure that left a session behind: this page began hydrating the store, or
    // the URL handed supabase-js a grant nobody here asked for. loadSession sets
    // `userId` BEFORE the profile / roles / assignment fetches, so a half-hydrated
    // store is still `isAuthenticated` with no orgs: "Back to login" would hit Login's
    // already-authenticated effect, bounce to /buildings, and ProtectedRoute would
    // forward an existing member on to /create-organization — the very bounce this
    // page exists to prevent. logout() never rejects, and clears the store up front — before
    // its own await — so it drops `userId` whether signOut fails, or hangs on the auth lock.
    //
    // The earlier paths — bar the forged grant, see failRejected — issue no sign-out, which
    // is worth stating precisely because
    // supabase-js has already had its say before this page renders. A provider error
    // arrives as `error_description` in the hash, `_isImplicitGrantCallback` matches on
    // that alone, and `_initialize()` then calls `_removeSession()` for every error code
    // but the three identity_* ones — so a signed-in user who cancels at the consent
    // screen is already signed out locally, and skipping logout() here preserves nothing.
    // Where it does bite is every path that gets here without a session of its own — the
    // grant gate failing, or getSession erroring, returning nothing, or throwing. Either
    // gotrue kept the session it recovered from storage, which predates this attempt and is
    // not this page's to drop, or `_getSessionFromURL` failed and `_initialize()` already
    // dropped it via `_removeSession()`. Either way this attempt left nothing behind.
    //
    // Scoped "local" deliberately: the intent is to drop THIS tab's half-hydrated state,
    // and the trigger may still be transient (a membership query erroring twice in a row
    // — run() retries once before reaching here, so this is never a single blip). auth-js's
    // default `scope: 'global'` would revoke the user's refresh tokens everywhere, so a
    // blip here would sign them out of their phone too.
    const failAndSignOut = async () => {
      await authStore.logout("local");
      showFailure();
    };

    // A refusal settled by the URL alone. Most of these leave nothing behind — see
    // failAndSignOut. The forged grant is the exception: supabase-js adopted that session
    // while the client initialised, before this page mounted, so this browser is already
    // holding an account nobody here signed into. Showing a failure screen over it is not
    // enough; it has to go.
    const failRejected = async (rejection: CallbackRejection) => {
      logger.warn("auth", rejection.code, rejection.message, rejection.data);
      if (rejection.forgedGrant) await failAndSignOut();
      else showFailure();
    };

    // One immediate retry before the destructive path in run(), same as loadPermissions does.
    // The usual trigger is transient — fetchOrgMembership fires three concurrent queries and
    // reports the first error any of them returns, so a single dropped request or 5xx would
    // otherwise cost a sign-in that actually succeeded and make the user redo the whole OAuth
    // round trip. A real denial (42501) or a missing profile row just fails again one round
    // trip later, well inside CALLBACK_TIMEOUT_MS.
    //
    // SIGNED_OUT_MID_LOAD is the exception: there the store was cleared by a sign-out that
    // landed mid-flight, so a retry would hydrate the very session that sign-out dropped.
    // ponytail: no backoff and no transport/denial classification — one retry covers the
    // blip; give it a real backoff only if reports show a slower race.
    const hydrateStore = async () => {
      const failure = await authStore.loadSession();
      if (cancelled || !failure || failure === SIGNED_OUT_MID_LOAD) return failure;

      logger.warn("auth", "oauth-callback-store-retry", "Auth store hydration failed, retrying", {
        error: failure,
      });
      return authStore.loadSession();
    };

    // The session this redirect produced, or null once the failure is on screen (or the mount
    // was cancelled, which showFailure and the caller both already respect).
    const readSession = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (cancelled) return null;

      if (error || !data.session) {
        logger.warn("auth", "oauth-callback-no-session", "No session after OAuth redirect", {
          error: error?.message,
        });
        showFailure();
        return null;
      }

      return data.session;
    };

    const run = async () => {
      // A mount for a grant this page load already accepted — a browser Back onto the callback
      // entry, which still carries the fragment. The round-trip state is spent by then, so
      // re-judging the grant would classify the user's OWN successful sign-in as forged and
      // sign them out. Forward instead: the callback is not a page to return to.
      if (initialGrantWasAccepted()) {
        settled = true;
        navigate(next, { replace: true });
        return;
      }

      // Rejected on the URL alone, before any session is read.
      const rejection = readCallbackRejection(searchParams);

      if (rejection) {
        await failRejected(rejection);
        return;
      }

      const session = await readSession();
      if (!session) return;

      // AuthProvider's mount-time loadSession() races this page; calling it again
      // is idempotent and guarantees the store is hydrated before we navigate
      // (same pattern as InviteAccept / CreateOrganization).
      hydrating = true;
      const hydrationError = await hydrateStore();
      if (cancelled) return;

      // loadSession never rejects — it catches every failure and returns it. Read that
      // return value, never authStore.error: AuthProvider's parallel loadSession writes
      // the same slot, so a transient failure of ITS membership queries would fail a
      // sign-in that actually succeeded (and the reverse ordering would hide a real
      // failure). A failed profile / roles / assignments fetch would otherwise navigate
      // on with empty orgRoles, and ProtectedRoute would bounce an existing member to
      // /create-organization. Fail visibly instead.
      if (hydrationError) {
        logger.warn("auth", "oauth-callback-store-error", "Auth store failed to hydrate", {
          error: hydrationError,
        });
        await failAndSignOut();
        return;
      }

      logger.info("auth", "oauth-callback-success", "OAuth sign-in completed", {
        userId: (session.user.id || "unknown").substring(0, 8) + "...",
      });
      // Google's sign-up and sign-in come back through this same redirect and supabase reports
      // nothing that separates them, so read it off the two server-side stamps instead. A first
      // sign-in is the request that MINTS the auth user, and GoTrue writes last_sign_in_at in
      // that same request (measured against local GoTrue: 25 ms apart) while moving it forward
      // on every later sign-in. Both stamps come from the server, so — unlike a wall-clock
      // window against `Date.now()` — a slow consent screen cannot demote a real sign-up, and a
      // password user who links Google minutes later is not counted as a second sign-up.
      identifyUser(session.user.id);
      // A missing stamp is NOT a sign-up: GoTrue writes last_sign_in_at in the request that
      // mints the user, so every real sign-up has one. Coalescing it to created_at made the
      // delta 0 and reported `signed_up` for whatever returning user arrives without it —
      // the one direction this must never guess in, since a sign-up is unrepeatable.
      const { created_at: createdAt, last_sign_in_at: lastSignInAt } = session.user;
      if (lastSignInAt != null && Date.parse(lastSignInAt) - Date.parse(createdAt) < 5_000) {
        track("signed_up", { method: "google", has_invite: next.startsWith("/invite/accept") });
      } else {
        track("signed_in", { method: "google" });
      }
      // This round trip has landed; the state it travelled with is spent. Kept until here
      // rather than consumed by the check — see endOAuthRoundTrip. Latching the grant beside
      // it is what stops a later mount re-judging a URL whose state no longer exists.
      endOAuthRoundTrip();
      markInitialGrantAccepted();
      settled = true;
      navigate(next, { replace: true });
    };

    // The escape hatch for an await that never settles (see CALLBACK_TIMEOUT_MS): give the
    // user the "Back to login" button instead of a spinner that never ends.
    const timeout = setTimeout(() => {
      if (settled || cancelled) return;

      logger.warn("auth", "oauth-callback-timeout", "OAuth callback did not settle in time", {
        timeoutMs: CALLBACK_TIMEOUT_MS,
        hydrating,
      });
      setState("failed");
      // Whatever is stuck may still settle later; from here it must not act, which is the
      // `cancelled` contract run() already checks after every await. A getSession that
      // finally returns has to leave the failure screen the user is reading alone.
      cancelled = true;

      // Same reason as failAndSignOut — a half-hydrated store is `isAuthenticated` with no
      // orgs, so "Back to login" would bounce an existing member to /create-organization.
      // Not awaited: signOut takes the same lock that is stuck, and the failure UI cannot
      // wait on it. Nothing is lost by not awaiting — logout() clears the store BEFORE that
      // request (AuthStore.clearAuthState), which is what makes this work on the very
      // deadlock it is written for; only the server-side revoke is left pending.
      // loadSession finishing afterwards only re-hydrates a valid session.
      if (hydrating) void authStore.logout("local");
    }, CALLBACK_TIMEOUT_MS);

    // getSession() rejects on storage / navigator.locks failures and loadSession()
    // is an MST flow that rejects on an unexpected throw. Without this the rejection
    // escapes unhandled and the page spins on "verifying" forever.
    run().catch((thrown: unknown) => {
      if (cancelled) return;
      logger.error(
        "auth",
        "oauth-callback-threw",
        "OAuth callback failed unexpectedly",
        thrown instanceof Error ? thrown : new Error(String(thrown))
      );
      if (hydrating) void failAndSignOut();
      else showFailure();
    });
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [searchParams, next, navigate, authStore]);

  /**
   * The way off the failure screen — and the one place a session that predates this attempt
   * IS dropped.
   *
   * Nothing in the effect signs that session out (see failAndSignOut), and AuthProvider's
   * mount-time loadSession() hydrates it in parallel with this page, so the store can sit here
   * authenticated as somebody else. `/login` then redirects an authenticated user straight on
   * to `returnUrl`: on a shared browser, whoever clicked "Continue with Google" and landed on
   * this screen would reach the previous user's account in two clicks — the outcome
   * hasImplicitGrant refuses to navigate to. Signing out only here keeps that automatic and
   * this deliberate: it takes the user's click, and it ends the session on the browser they
   * are both sitting at.
   *
   * "local" for the same reason failAndSignOut uses it — the trigger may be a transient
   * failure of the user's own sign-in, which must not revoke the refresh tokens on their
   * phone. Not awaited: logout() clears the store before its own request
   * (AuthStore.clearAuthState), so the navigation below already lands on a signed-out Login
   * even when signOut hangs on the auth lock — the very case the timeout above exists for.
   */
  const backToLogin = () => {
    if (authStore.isAuthenticated) void authStore.logout("local");
    navigate("/login", { replace: true });
  };

  return (
    <AuthPageLayout>
      <AuthPageHeader
        title={state === "verifying" ? t.auth.signingIn : t.auth.googleSignInFailed}
        description={state === "verifying" ? t.auth.signingInDesc : t.auth.googleSignInFailedDesc}
      />

      {state === "verifying" ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <Button variant="outline" className={AUTH_SECONDARY_ACTION_CLASSES} onClick={backToLogin}>
          {t.auth.backToLogin}
        </Button>
      )}
    </AuthPageLayout>
  );
}
